import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Trash2, Plus } from 'lucide-react';
import { API } from '../config';

const playfair = "'Playfair Display', serif";
const cardStyle = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' };
const inputStyle = { background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: '#F8FAF9' };

const fmtIDR = (n) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n || 0);

export const TiersManager = () => {
  const [tiers, setTiers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetch(); }, []);

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/tiers`, { withCredentials: true });
      setTiers(data.tiers || []);
    } catch (e) {
      toast.error('Gagal memuat tier');
    } finally { setLoading(false); }
  };

  const update = (idx, field, value) => {
    const updatedValue = field === 'min_spending_idr' ? (parseInt(value, 10) || 0) : value;
    setTiers((prev) => {
      const copy = prev.map((t) => ({ ...t }));
      copy[idx] = { ...copy[idx], [field]: updatedValue };
      return copy;
    });
  };

  const removeRow = (idx) => {
    if (!window.confirm(`Hapus tier "${tiers[idx].name}"?`)) return;
    setTiers(tiers.filter((_, i) => i !== idx));
  };

  const addRow = () => {
    setTiers([...tiers, { name: 'New Tier', min_spending_idr: 0, color: '#A3B8AC', benefits: '' }]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/tiers`, { tiers }, { withCredentials: true });
      toast.success('Tier settings tersimpan');
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-center py-12" style={{ color: '#A3B8AC' }}>Loading...</div>;

  return (
    <div className="space-y-5" data-testid="tiers-manager">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-light" style={{ fontFamily: playfair, color: '#F8FAF9' }}>Customer Tiers</h2>
          <p className="text-xs mt-1" style={{ color: '#A3B8AC' }}>
            Atur tier berdasarkan lifetime spending (IDR). Tier dihitung otomatis dari total approved top-up tiap user.
            Benefits diisi bebas — akan tampil di profile customer.
          </p>
        </div>
        <button onClick={addRow} className="px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-1.5"
          style={{ background: 'rgba(212,175,55,0.2)', color: '#D4AF37', border: '1px solid #D4AF37' }}
          data-testid="add-tier-btn">
          <Plus className="w-3.5 h-3.5" /> Tambah Tier
        </button>
      </div>

      <div className="space-y-3">
        {tiers.map((t, idx) => (
          <div key={idx} className="rounded-2xl p-4 backdrop-blur-xl" style={cardStyle} data-testid={`tier-row-${idx}`}>
            <div className="grid md:grid-cols-12 gap-3 mb-3">
              <div className="md:col-span-2">
                <label className="block text-xs mb-1" style={{ color: '#A3B8AC' }}>Nama Tier</label>
                <input value={t.name} onChange={(e) => update(idx, 'name', e.target.value)}
                  className="w-full rounded-lg px-3 py-2 outline-none text-sm" style={inputStyle}
                  data-testid={`tier-name-${idx}`} />
              </div>
              <div className="md:col-span-3">
                <label className="block text-xs mb-1" style={{ color: '#A3B8AC' }}>Min Spending (IDR)</label>
                <input type="number" value={t.min_spending_idr} onChange={(e) => update(idx, 'min_spending_idr', e.target.value)}
                  className="w-full rounded-lg px-3 py-2 outline-none text-sm" style={inputStyle}
                  data-testid={`tier-min-${idx}`} />
                <p className="text-[10px] mt-0.5" style={{ color: '#6B8079' }}>= {fmtIDR(t.min_spending_idr)}</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs mb-1" style={{ color: '#A3B8AC' }}>Warna</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={t.color || '#A3B8AC'} onChange={(e) => update(idx, 'color', e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer" data-testid={`tier-color-${idx}`} />
                  <span className="text-xs px-2 py-1 rounded" style={{ background: t.color, color: '#080C0A', fontWeight: 600 }}>
                    {t.name}
                  </span>
                </div>
              </div>
              <div className="md:col-span-5 flex items-end justify-end">
                <button onClick={() => removeRow(idx)} className="p-2.5 rounded-lg"
                  style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444' }} data-testid={`tier-remove-${idx}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <label className="block text-xs mb-1" style={{ color: '#A3B8AC' }}>Benefits (akan tampil di profile customer)</label>
            <textarea value={t.benefits || ''} onChange={(e) => update(idx, 'benefits', e.target.value)}
              rows={2}
              placeholder="cth: 'Free ongkir untuk Java + Bali, Cashback 5%, Akses Kuji eksklusif'"
              className="w-full rounded-lg px-3 py-2 outline-none text-sm" style={inputStyle}
              data-testid={`tier-benefits-${idx}`} />
          </div>
        ))}
      </div>

      <button onClick={save} disabled={saving}
        className="px-6 py-3 rounded-full font-semibold flex items-center gap-2 disabled:opacity-50"
        style={{ background: '#D4AF37', color: '#080C0A' }} data-testid="save-tiers-btn">
        <Save className="w-4 h-4" /> {saving ? 'Menyimpan…' : 'Simpan Semua Tier'}
      </button>
    </div>
  );
};
